import { Form, Link, useLoaderData, type LoaderFunctionArgs } from 'react-router';
import { TIERS, TIER_DISPLAY_NAME, SUB_STATUSES } from '@/types';
import { api, toQuery } from '@/lib/api';
import { TierBadge } from '@/components/tier-badge';
import { StatusChip, SuppressedChip } from '@/components/status-chip';
import { Pagination } from '@/components/pagination';
import { RowLink } from '@/components/row-link';
import { displayName, fmtAgo, fmtDate } from '@/lib/format';

type Contact = {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  businessName: string | null;
  tier: string;
  subStatus: string | null;
  organizationName: string | null;
  source: string;
  tags: string[];
  suppressedReason: string | null;
  ffCreatedAt: string | null;
  lastActivityAt: string | null;
  trialEnd: string | null;
};

export async function contactsLoader({ request }: LoaderFunctionArgs) {
  const sp = new URL(request.url).searchParams;
  const get = (k: string) => sp.get(k) ?? undefined;
  const query = {
    q: get('q'),
    tier: get('tier'),
    subStatus: get('subStatus'),
    source: get('source'),
    suppressed: get('suppressed'),
    segmentId: get('segmentId'),
    tag: get('tag'),
    sort: get('sort') ?? 'ffCreatedAt',
    dir: get('dir') ?? 'desc',
    page: Number(get('page') ?? 1),
    pageSize: 50,
  };
  const [data, stats] = await Promise.all([
    api<{ items: Contact[]; total: number; page: number; pageSize: number }>(`/contacts${toQuery(query)}`),
    api<{ total: number; suppressed: number; leads: number; byTier: Record<string, number> }>('/contacts/stats'),
  ]);
  return { query, data, stats };
}

export function ContactsPage() {
  const { query, data, stats } = useLoaderData<typeof contactsLoader>();
  const makeHref = (page: number) => `/contacts${toQuery({ ...query, page })}`;

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl">Contacts</h1>
          <p className="text-sm text-muted-foreground">
            {stats.total} contacts · {stats.leads} leads · {stats.suppressed} suppressed ·{' '}
            {TIERS.map((t) => `${TIER_DISPLAY_NAME[t]} ${stats.byTier[t] ?? 0}`).join(' · ')}
          </p>
        </div>
      </div>

      <Form key={JSON.stringify(query)} className="flex flex-wrap items-end gap-3 rounded-lg border bg-white p-3 text-sm" method="get">
        <label className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Search</span>
          <input name="q" defaultValue={query.q ?? ''} placeholder="email, name, business" className="h-9 w-56 rounded-md border px-2" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Plan</span>
          <select name="tier" defaultValue={query.tier ?? ''} className="h-9 rounded-md border px-2">
            <option value="">Any</option>
            {TIERS.map((t) => (
              <option key={t} value={t}>
                {TIER_DISPLAY_NAME[t]}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Status</span>
          <select name="subStatus" defaultValue={query.subStatus ?? ''} className="h-9 rounded-md border px-2">
            <option value="">Any</option>
            {SUB_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Source</span>
          <select name="source" defaultValue={query.source ?? ''} className="h-9 rounded-md border px-2">
            <option value="">Any</option>
            <option value="facetflow">Facet Flow user</option>
            <option value="import">Imported lead</option>
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Suppressed</span>
          <select name="suppressed" defaultValue={query.suppressed ?? ''} className="h-9 rounded-md border px-2">
            <option value="">Any</option>
            <option value="false">No</option>
            <option value="true">Yes</option>
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Sort</span>
          <select name="sort" defaultValue={query.sort} className="h-9 rounded-md border px-2">
            <option value="ffCreatedAt">Signed up</option>
            <option value="lastActivityAt">Last activity</option>
            <option value="tier">Plan</option>
            <option value="email">Email</option>
          </select>
        </label>
        {query.segmentId && <input type="hidden" name="segmentId" value={query.segmentId} />}
        <button className="h-9 rounded-md bg-[var(--ff-primary)] px-4 text-white">Filter</button>
        <Link to="/contacts" className="ff-link text-xs">
          Reset
        </Link>
      </Form>

      <div className="overflow-x-auto rounded-lg border bg-white">
        <table className="data-table">
          <thead>
            <tr>
              <th>Contact</th>
              <th>Plan</th>
              <th>Status</th>
              <th>Org</th>
              <th>Tags</th>
              <th>Signed up</th>
              <th>Last activity</th>
            </tr>
          </thead>
          <tbody>
            {data.items.length === 0 && (
              <tr>
                <td colSpan={7} className="py-8 text-center text-muted-foreground">
                  No contacts match. {stats.total === 0 && 'Run the sync job (POST /jobs/sync-ff) to import Facet Flow users.'}
                </td>
              </tr>
            )}
            {data.items.map((c) => (
              <RowLink key={c.id} href={`/contacts/${c.id}`}>
                <td>
                  <Link to={`/contacts/${c.id}`} className="ff-link font-medium">
                    {displayName(c)}
                  </Link>
                  <div className="text-xs text-muted-foreground">
                    {c.email}
                    {c.businessName ? ` · ${c.businessName}` : ''}
                    {c.source === 'import' ? ' · lead' : ''}
                  </div>
                </td>
                <td>
                  <TierBadge tier={c.tier} trialing={c.subStatus === 'trialing'} />
                </td>
                <td className="space-x-1">
                  <StatusChip status={c.subStatus} />
                  <SuppressedChip reason={c.suppressedReason} />
                </td>
                <td className="text-muted-foreground">{c.organizationName ?? '—'}</td>
                <td className="space-x-1">
                  {c.tags.map((t) => (
                    <span key={t} className="rounded bg-muted px-1.5 py-0.5 text-xs">
                      {t}
                    </span>
                  ))}
                </td>
                <td className="whitespace-nowrap">{fmtDate(c.ffCreatedAt)}</td>
                <td className="whitespace-nowrap">{fmtAgo(c.lastActivityAt)}</td>
              </RowLink>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination page={data.page} pageSize={data.pageSize} total={data.total} makeHref={makeHref} />
    </div>
  );
}
